/**
 * Custom hook for tournament data persistence
 * Handles localStorage operations and JSON serialization
 */

import { useState, useEffect, useCallback } from 'react';
import type { Tournament, Player } from '../types';
import { STORAGE_KEY } from '../types';

// Helper to convert Player Sets to Arrays for JSON storage
export const serializePlayer = (player: Player): Player => ({
  ...player,
  partnerHistory: Array.isArray(player.partnerHistory) 
    ? player.partnerHistory 
    : Array.from(player.partnerHistory || []),
  opponentHistory: Array.isArray(player.opponentHistory) 
    ? player.opponentHistory 
    : Array.from(player.opponentHistory || [])
});

// Helper to deserialize players (ensure arrays)
export const deserializePlayer = (player: Player): Player => ({
  ...player,
  partnerHistory: player.partnerHistory || [],
  opponentHistory: player.opponentHistory || []
});

export function useTournamentStorage() {
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Deserialize players
        if (parsed.players) {
          parsed.players = parsed.players.map(deserializePlayer);
        }
        setTournament(parsed);
      }
    } catch (error) {
      console.error('Failed to load tournament:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Save to localStorage whenever tournament changes
  useEffect(() => {
    if (tournament) {
      try {
        // Serialize players before storing
        const toStore = {
          ...tournament,
          players: tournament.players.map(serializePlayer)
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
      } catch (error) {
        console.error('Failed to save tournament:', error);
      }
    }
  }, [tournament]);

  const saveTournament = useCallback((newTournament: Tournament) => {
    setTournament(newTournament);
  }, []);

  const updateTournament = useCallback((updates: Partial<Tournament>) => {
    setTournament(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const resetTournament = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setTournament(null);
  }, []);

  const exportTournament = useCallback(() => {
    if (!tournament) return null;
    return JSON.stringify({
      ...tournament,
      players: tournament.players.map(serializePlayer)
    }, null, 2);
  }, [tournament]);

  const importTournament = useCallback((jsonString: string): boolean => {
    try {
      const parsed = JSON.parse(jsonString);
      if (parsed.players) {
        parsed.players = parsed.players.map(deserializePlayer);
      }
      // Validate required fields
      if (!parsed.id || !parsed.config || !parsed.players) {
        return false;
      }
      setTournament(parsed);
      return true;
    } catch (error) {
      console.error('Failed to import tournament:', error);
      return false;
    }
  }, []);

  return {
    tournament,
    loading,
    saveTournament,
    updateTournament,
    resetTournament,
    exportTournament,
    importTournament
  };
}